import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { HimiDashboardReq } from '../../model/inko-backend.model'
import { DashboardZeitraeume, mitAktuellenZeitraeumen } from '../../util/dashboard-filter'

/** Der Filter samt der Marken mitlaufender Zeitraeume (`DashboardZeitraeume`). */
export type HimiDashboardFilter = HimiDashboardReq & DashboardZeitraeume

/**
 * „Verordnung fehlt“ war bis 2026-09-30 ein eigener Filter-Abschnitt (`withOpenVerordnungNachreichenTask`),
 * seitdem ist es der Anlagestatus `VERORDNUNG_AUSSTEHEND`. Filter und Kacheln liegen im Browser
 * gespeichert: ein alter Stand traegt den Schalter noch, und ohne Uebersetzung wuerde er weiter
 * filtern, ohne dass ein Chip oder eine Checkbox ihn zeigt. Deshalb laeuft jeder gelesene und jeder
 * geschriebene Filter hier durch. Beide Formen treffen im Backend dieselbe Bedingung.
 */
export function mitVerordnungFehltAlsAnlagestatus(filter: HimiDashboardFilter): HimiDashboardFilter {
  const { withOpenVerordnungNachreichenTask: alterSchalter, ...rest } = filter
  if (!alterSchalter || rest.anlageStatus?.includes('VERORDNUNG_AUSSTEHEND')) {
    return rest
  }
  return { ...rest, anlageStatus: [...(rest.anlageStatus ?? []), 'VERORDNUNG_AUSSTEHEND'] }
}

/* Model */

export interface HimiDashboardStateModel {
  version: number

  himiColumns: {
    keys: string[]
    visible: string[]
  }
  himiFilter: HimiDashboardFilter
  /**
   * Schluessel der fest eingebauten Kacheln, die der Nutzer entfernt hat. Bewusst eine Liste der
   * ENTFERNTEN statt der sichtbaren: so erreichen spaeter ergaenzte Preset-Kacheln auch Nutzer,
   * die ihre Kacheln schon einmal angepasst haben.
   */
  himiDismissedPresets?: string[]
  himiKpiCards: {
    title: string
    himiFilter: HimiDashboardFilter
  }[]
}

export const himiAvailableColumns: { label: string; key: string; defaultVisible?: boolean }[] = [
  { label: 'Datum', key: 'createdDate', defaultVisible: true },
  { label: 'Patientenname', key: 'patientenname', defaultVisible: true },
  { label: 'Geburtsdatum', key: 'dateOfBirth' },
  { label: 'Krankenkasse', key: 'krankenkasse', defaultVisible: true },
  { label: 'Versicherungsart', key: 'versicherungsart' },
  { label: 'Zuzahlungsbefreiung', key: 'zuzahlungsbefreiung' },
  { label: 'Herkunft', key: 'externerPartner', defaultVisible: true },
  { label: 'Produktgruppe', key: 'produktgruppe', defaultVisible: true },
  { label: 'Versorgungstyp', key: 'versorgungstyp', defaultVisible: false },
  { label: 'Folgeversorgung', key: 'folgeversorgungstatus' },
  { label: 'Anlagestatus', key: 'anlagestatus', defaultVisible: true },
  { label: 'Genehmigungsstatus', key: 'genehmigungsstatus', defaultVisible: true },
  { label: 'Versorgungsstatus', key: 'versorgungsstatus', defaultVisible: true },
  { label: 'Lieferungsstatus', key: 'lieferungsstatus', defaultVisible: true },
]

export const himiColumnNameMap = himiAvailableColumns.reduce(
  (acc, col) => {
    acc[col.key] = col.label
    return acc
  },
  {} as Record<string, string>,
)

/* Actions */

export class DashboardStateHimiUpdateColumns {
  static readonly type = '[Dashboard] Update HiMi Columns'
}

export class DashboardStateHimiMoveColumns {
  static readonly type = '[Dashboard] Move HiMi Columns'
  constructor(
    public indexA: number,
    public indexB: number,
  ) {}
}

export class DashboardStateHimiSetColumnVisible {
  static readonly type = '[Dashboard] Visible HiMi Column'
  constructor(public keys: string[]) {}
}

export class DashboardStateHimiReset {
  static readonly type = '[Dashboard] Reset HiMi Columns'
}

export class DashboardStateHimiSetFilter {
  static readonly type = '[Dashboard] Set HiMi Filter'
  constructor(public filter: Partial<HimiDashboardFilter>) {}
}

export class DashboardStateHimiResetFilter {
  static readonly type = '[Dashboard] Reset HiMi Filter'
}

export class DashboardStateHimiAddKpiCard {
  static readonly type = '[Dashboard] Add HiMi KPI Card'
  constructor(
    public title: string,
    public filter: HimiDashboardFilter,
  ) {}
}

export class DashboardStateHimiRemoveKpiCard {
  static readonly type = '[Dashboard] Remove HiMi KPI Card'
  constructor(public index: number) {}
}

export class DashboardStateHimiDismissPresetKpiCard {
  static readonly type = '[Dashboard] Dismiss Himi Preset KPI Card'
  constructor(public key: string) {}
}
export class DashboardStateHimiRestorePresetKpiCards {
  static readonly type = '[Dashboard] Restore Himi Preset KPI Cards'
}

/* State */

const defaultState: HimiDashboardStateModel = {
  version: 3,

  himiColumns: {
    keys: himiAvailableColumns.map((c) => c.key),
    visible: himiAvailableColumns.filter((c) => c.defaultVisible).map((c) => c.key),
  },
  himiFilter: {
    pageSize: 30,
  },
  himiDismissedPresets: [],
  himiKpiCards: [],
}

@State<HimiDashboardStateModel>({
  name: 'himiDashboard',
  defaults: defaultState,
})
@Injectable()
export class HimiDashboardState {
  @Selector()
  static himiAvailableColumns(state: HimiDashboardStateModel) {
    return state.himiColumns.keys
  }

  @Selector()
  static himiVisibleColumns(state: HimiDashboardStateModel) {
    return state.himiColumns.visible
  }

  @Selector()
  static himiFilter(state: HimiDashboardStateModel) {
    // Mitlaufende Zeitraeume („Letzter Monat“) auf heute ausrechnen — der persistierte Stand kann von gestern sein.
    return mitAktuellenZeitraeumen(mitVerordnungFehltAlsAnlagestatus(state.himiFilter))
  }

  @Selector()
  static himiKpiCards(state: HimiDashboardStateModel) {
    return state.himiKpiCards.map((k) => ({ ...k, himiFilter: mitVerordnungFehltAlsAnlagestatus(k.himiFilter) }))
  }

  @Selector()
  static himiDismissedPresets(state: HimiDashboardStateModel): string[] {
    // Persistierter Altstand kennt das Feld nicht — ohne Fallback waere es undefined.
    return state.himiDismissedPresets ?? []
  }

  @Selector()
  static himiFilterCounter(state: HimiDashboardStateModel) {
    const filter = mitVerordnungFehltAlsAnlagestatus(state.himiFilter)
    return (
      (filter.versorgungsStatus?.length ?? 0) +
      (filter.folgeversorgungStatus?.length ?? 0) +
      (filter.produktgruppen?.length ?? 0) +
      (filter.himiVersorgungsType ? 1 : 0) +
      (filter.healthInsuranceIds?.length ?? 0) +
      (filter.externalPartnerIds?.length ?? 0) +
      (filter.insuranceType ? 1 : 0) +
      (filter.openTaskForAssignee ? 1 : 0) +
      (filter.withOpenFolgeversorgungTask ? 1 : 0) +
      (filter.wartetAufUnterschrift ? 1 : 0) +
      (typeof filter.vomCallcenterAngelegt === 'boolean' ? 1 : 0) +
      (filter.anlageStatus?.length ?? 0) +
      (filter.genehmigungStatus?.length ?? 0) +
      (filter.ekvRequestFromDate || filter.ekvRequestUntilDate ? 1 : 0) +
      (filter.fromCreatedDate || filter.toCreatedDate ? 1 : 0)
    )
  }

  @Action(DashboardStateHimiUpdateColumns)
  setHimiColumns(ctx: StateContext<HimiDashboardStateModel>) {
    const { version } = ctx.getState()
    if (version != defaultState.version) {
      ctx.setState({
        ...defaultState,
        himiKpiCards: ctx.getState().himiKpiCards ?? defaultState.himiKpiCards,
      })
      return
    }

    const columns = ctx.getState().himiColumns.keys.slice()
    const allColumnKeys = himiAvailableColumns.map((c) => c.key)

    allColumnKeys.forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key)
      }
    })
    const filteredColumns = columns.filter((key) => allColumnKeys.includes(key))

    ctx.patchState({
      himiColumns: {
        ...ctx.getState().himiColumns,
        keys: filteredColumns,
      },
    })
  }

  @Action(DashboardStateHimiMoveColumns)
  moveHimiColumns(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiMoveColumns) {
    const columns = ctx.getState().himiColumns.keys.slice()
    const [movedColumn] = columns.splice(action.indexA, 1)
    columns.splice(action.indexB, 0, movedColumn)
    ctx.patchState({
      himiColumns: {
        ...ctx.getState().himiColumns,
        keys: columns,
      },
    })
  }

  @Action(DashboardStateHimiSetColumnVisible)
  setHimiColumnVisible(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiSetColumnVisible) {
    ctx.patchState({
      himiColumns: {
        ...ctx.getState().himiColumns,
        visible: action.keys,
      },
    })
  }

  @Action(DashboardStateHimiReset)
  resetHimiColumns(ctx: StateContext<HimiDashboardStateModel>) {
    ctx.patchState({
      himiColumns: defaultState.himiColumns,
    })
  }

  @Action(DashboardStateHimiSetFilter)
  setHimiFilter(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiSetFilter) {
    ctx.patchState({
      himiFilter: mitVerordnungFehltAlsAnlagestatus({
        ...ctx.getState().himiFilter,
        ...action.filter,
      }),
    })
  }

  @Action(DashboardStateHimiResetFilter)
  resetHimiFilter(ctx: StateContext<HimiDashboardStateModel>) {
    ctx.patchState({
      himiFilter: defaultState.himiFilter,
    })
  }

  @Action(DashboardStateHimiAddKpiCard)
  addHimiKpiCard(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiAddKpiCard) {
    ctx.patchState({
      himiKpiCards: [
        ...ctx.getState().himiKpiCards,
        {
          title: action.title,
          himiFilter: action.filter,
        },
      ],
    })
  }

  @Action(DashboardStateHimiRemoveKpiCard)
  removeHimiKpiCard(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiRemoveKpiCard) {
    const currentCards = ctx.getState().himiKpiCards.slice()
    currentCards.splice(action.index, 1)
    ctx.patchState({
      himiKpiCards: currentCards,
    })
  }
  @Action(DashboardStateHimiDismissPresetKpiCard)
  dismissHimiPresetKpiCard(ctx: StateContext<HimiDashboardStateModel>, action: DashboardStateHimiDismissPresetKpiCard) {
    const dismissed = ctx.getState().himiDismissedPresets ?? []
    if (dismissed.includes(action.key)) {
      return
    }
    ctx.patchState({ himiDismissedPresets: [...dismissed, action.key] })
  }

  @Action(DashboardStateHimiRestorePresetKpiCards)
  restoreHimiPresetKpiCards(ctx: StateContext<HimiDashboardStateModel>) {
    // Nur die Presets kommen zurueck; selbst angelegte Kacheln bleiben unangetastet.
    ctx.patchState({ himiDismissedPresets: [] })
  }
}
